import { useState } from "react";
import { Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import ZButton from "@/components/ZButton";
import { ZInput } from "@/components/ZInput";

/**
 * Discreet prompt shown to a guest after a successful action (reservation,
 * contribution, purchase…). Lets them request a magic-link email to claim
 * their actions later — no password required.
 *
 * Props:
 *  - email (string, optional): pre-fill the email field if known
 *  - name (string, optional): pre-fill the name field
 *  - next (string, optional): redirect path after magic-link verify (default /engagements)
 *  - context (string, optional): short description shown to user ("votre cadeau", "vos billets", …)
 */
export default function GuestClaimPrompt({
  email: initialEmail = "",
  name: initialName = "",
  next = "/engagements",
  context = "vos actions",
}) {
  const [email, setEmail] = useState(initialEmail || "");
  const [name, setName] = useState(initialName || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const e = (email || "").trim();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Veuillez indiquer un e-mail valide.");
      return;
    }
    setSending(true);
    try {
      await api.post("/auth/magic-link/request", {
        email: e,
        name: (name || "").trim() || null,
        next,
      });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="guest-claim-sent" style={{
        background: "#E8F5E9", border: "1px solid #A5D6A7", borderRadius: 12,
        padding: 14, marginTop: 12, fontSize: 13, color: "#0F1418",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#166534", marginBottom: 4 }}>
          <Mail size={15} /> E-mail envoyé !
        </div>
        Cliquez sur le lien que vous venez de recevoir à <strong>{email}</strong> pour vous connecter en un clic.
      </div>
    );
  }

  return (
    <div data-testid="guest-claim-prompt" style={{
      background: "linear-gradient(135deg, #FCE2C3 0%, #FFF7EB 100%)",
      border: "1px solid #DA710133", borderRadius: 12,
      padding: 14, marginTop: 12, fontSize: 13,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, color: "#DA7101", marginBottom: 6 }}>
        <Sparkles size={15} /> Retrouvez {context} plus tard
      </div>
      <p style={{ color: "#5B6671", margin: "0 0 10px", lineHeight: 1.5 }}>
        Pas de mot de passe ! Nous vous envoyons un lien de connexion sécurisé pour
        accéder à vos billets, cadeaux et contributions quand vous voulez.
      </p>
      <div style={{ display: "grid", gap: 8 }}>
        <ZInput
          type="text" placeholder="Votre prénom (optionnel)"
          value={name} onChange={(e) => setName(e.target.value)}
          data-testid="guest-claim-name"
        />
        <ZInput
          type="email" placeholder="votre@email.com"
          value={email} onChange={(e) => setEmail(e.target.value)}
          data-testid="guest-claim-email"
        />
        <ZButton onClick={submit} loading={sending} block data-testid="guest-claim-submit">
          M&apos;envoyer le lien de connexion
        </ZButton>
      </div>
    </div>
  );
}
